//
// # Schematic Symbol
//

// NPM Imports
import typia from "typia";

// Workspace Imports
import { Result, Context } from "CoreUtils";
import type { DepotGet } from "Depot";
import { schema } from "GenAlphaSchema";
// Local Imports
import { exhaust } from "../errors";
import { Validator } from "../errors";
import { Point, Bbox } from "../core";
import { Ident, IdentMap } from "../ident";
import { PortDir } from "./portdir";
import {
  TextFormat,
  Rectangle,
  Polygon,
  Ellipse,
  ElementKind,
  Element,
} from "../graphics";

// # Symbol
// Nothing un-serializable in there, really just an alias for the schema version.
export type Symbol = schema.Symbol;
export type SymbolJson = schema.Symbol;

// Class-level cache for resolved symbols, mirroring `Layout.layouts`.
// Populated by `Symbol.getSymbol` so repeated lookups for the same ident
// hit memory rather than the depot. Lives at module scope to avoid the
// const-self-reference cycle.
const symbolsCache = new IdentMap<Symbol>();

// Resolve a symbol by ident, mirroring `Layout.getLayout`:
//   1. Class-level cache hit → return cached.
//   2. Anything else → `depot.content.get`. The depot resolves builtins,
//      generator-synthesized symbols, and persisted symbols uniformly.
async function getSymbol(
  ctx: Context,
  ident: schema.Ident,
  depot: DepotGet,
  namespace: string
): Promise<Result<Symbol>> {
  const existing = symbolsCache.get(ident);
  if (existing) return Result.Ok(existing);

  const result = await depot.content.get(ctx, ident, namespace);
  if (result.err) {
    return Result.Err({
      why: "symbol-not-in-depot",
      ident,
      error: result.val,
    });
  }
  if (result.val.contentKind !== "symbol") {
    return Result.Err({
      why: "target-not-a-symbol",
      ident,
      contentKind: result.val.contentKind,
    });
  }
  symbolsCache.set(result.val);
  return Result.Ok(result.val);
}

// The `Symbol` "implementation block".
export const Symbol = {
  new: (ident: Ident): Symbol => ({
    schemaVersion: schema.SCHEMA_VERSION,
    contentKind: "symbol",
    ident,
    bbox: Bbox.empty(),
    graphics: [],
    pins: [],
    selectionBoxes: [],
    exprLabels: [],
  }),

  // Create from a partial definition.
  fromPartial: (partial: Partial<Symbol>): Symbol => {
    const ident = partial.ident || Ident.default();

    return {
      ...Symbol.new(ident),
      ...partial,
    };
  },

  // JSON conversions. As of this writing, no-ops.
  toJson(symbol: Symbol): SymbolJson {
    return symbol; // Works as-is.
  },
  fromJson(symbolJson: SymbolJson): Symbol {
    return symbolJson; // Works as-is.
  },
  // Bounding box of just the visible drawn body of the symbol — the union of
  // its `graphics` elements. Excludes pins and label locations, which is what
  // makes this tighter than `symbol.bbox`. Returns `undefined` if the symbol
  // has no graphics.
  graphicsBbox: (symbol: Symbol): Bbox | undefined => {
    if (symbol.graphics.length === 0) return undefined;
    return Bbox.getBboxUnion(symbol.graphics.map(Element.bbox));
  },

  // Ident-keyed cache of resolved symbols. Exposed so callers can pre-seed
  // entries (e.g., when symbols arrive via a side channel) before binding.
  symbols: symbolsCache,

  // See the standalone `getSymbol` function above.
  getSymbol,

  validator: new Validator(typia.createValidate<Symbol>()),
  stringify: typia.json.createStringify<Symbol>(),
};

export type SymbolPin = schema.symbol.SymbolPin;

export const SymbolPin = {
  default: (): SymbolPin => ({
    portName: "port0",
    width: 1,
    direction: PortDir.Input,
    shape: PinShape.default(),
    labelFmt: TextFormat.fromPartial({ loc: { x: 10, y: 0 } }),
  }),
  clone: (pin: SymbolPin): SymbolPin => ({
    ...pin,
    labelFmt: pin.labelFmt ? TextFormat.clone(pin.labelFmt) : undefined,
  }),
  // Bounding box for the connection element.
  // Note this does not include the label.
  bbox: (pin: SymbolPin): Bbox => {
    return PinShape.bbox(pin.shape);
  },
  bboxContains: (pin: SymbolPin, pt: Point): boolean => {
    return PinShape.bboxContains(pin.shape, pt);
  },
  validator: new Validator(typia.createValidate<SymbolPin>()),
  stringify: typia.json.createStringify<SymbolPin>(),
};

// The subset of graphics `Element`s that can be the "hit shape" for symbol pins.
export type PinShape = Rectangle | Polygon | Ellipse;

export const PinShape = {
  default: (): Rectangle => {
    return Rectangle.new({
      bbox: { min: { x: -5, y: -5 }, max: { x: 5, y: 5 } },
      filled: true,
    });
  },
  fromCenter: (center: Point): Rectangle => {
    const min = Point.add(center, { x: -5, y: -5 });
    const max = Point.add(center, { x: 5, y: 5 });
    return Rectangle.new({
      bbox: { min, max },
      filled: true,
    });
  },
  bbox: (shape: PinShape): Bbox => {
    switch (shape.kind) {
      case ElementKind.Rectangle:
        return Rectangle.bbox(shape);
      case ElementKind.Polygon:
        return Polygon.bbox(shape);
      case ElementKind.Ellipse:
        return Ellipse.bbox(shape);
      default:
        return exhaust(shape);
    }
  },

  // Get the center of the shapeent's bounding box
  bboxCenter: (shape: PinShape): Point => {
    switch (shape.kind) {
      case ElementKind.Rectangle:
        return Rectangle.bboxCenter(shape);
      case ElementKind.Polygon:
        return Polygon.bboxCenter(shape);
      case ElementKind.Ellipse:
        return Ellipse.bboxCenter(shape);
      default:
        return exhaust(shape);
    }
  },

  bboxContains: (shape: PinShape, pt: Point): boolean => {
    const bbox = PinShape.bbox(shape);
    return Bbox.contains(bbox, pt);
  },

  // Translate an shapeent by a delta
  translate: (shape: PinShape, delta: Point): PinShape => {
    switch (shape.kind) {
      case ElementKind.Rectangle:
        return Rectangle.translate(shape, delta);
      case ElementKind.Polygon:
        return Polygon.translate(shape, delta);
      case ElementKind.Ellipse:
        return Ellipse.translate(shape, delta);
      default:
        return exhaust(shape);
    }
  },
};
